import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { EnglishLabels, Profile_Tab_English } from "src/common/constants/englishLabels";
import { useEffect, useState } from "react";

import ArrowRightIcon from "src/resources/icons/ArrowRightIcon";
import { MobileNumberInput } from "src/common/mobileNumberInput";
import { ProfileDto } from "src/types/Profile";
import { ProfileService } from "src/rest/profileService";
// Verification temporarily disabled; keep service import commented for now.
import { MarriageProfileService } from "src/rest/marriageProfileService";
import { Profile_Tab_Hin } from "src/common/constants/hindiLabels";
import { RootState } from "src/store/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "src/common/constants/constantEnums";
import { UseAppToast } from "src/common/useAppToast";
import { RELATIONSHIP_OPTIONS } from "src/utils/matrimonyOptions";
import type { ProfileTabKey } from "src/pages/profile/profile.constants";

type DrawerViewKey = ProfileTabKey | "EDIT_MEMBER";

interface Props {
  view: DrawerViewKey;
  isOpen: boolean;
  onClose: () => void;
  userData: ProfileDto | null;
  onSave?: (updatedData: ProfileDto) => void;
  isConsultant?: boolean;
  selectedMember?: {
    id?: number;
    memberName?: string | null;
    relation?: string | null;
    email?: string | null;
    mobileNo?: string | null;
  } | null;
}

const RightAlignedDrawer: React.FC<Props> = ({
  view,
  isOpen,
  onClose,
  userData,
  onSave,
  isConsultant = false,
  selectedMember,
}) => {
  const [formData, setFormData] = useState<ProfileDto>({} as ProfileDto);
  const [familyMobile, setFamilyMobile] = useState("");
  const [memberName, setMemberName] = useState("");
  const [relation, setRelation] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [marriageStep, setMarriageStep] = useState<1 | 2>(1);
  const [registeringFor, setRegisteringFor] = useState<"SELF" | "FAMILY">("SELF");
  const [candidateRelationship, setCandidateRelationship] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [hasPermission, setHasPermission] = useState(false);
  const [acceptsTerms, setAcceptsTerms] = useState(false);

  // NEW: mandatory verification fields
  const [verifyDob, setVerifyDob] = useState("");
  const [verifyEmailId, setVerifyEmailId] = useState("");
  const [verifyMobileNo, setVerifyMobileNo] = useState("");
  const [isVerifyingProfile, setIsVerifyingProfile] = useState(false);

  const navigate = useNavigate();

  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const isHindiLabels = globalInfo.globalLanguage === "HI";

  // Local address state for prefill
  const [currentAddress, setCurrentAddress] = useState<any>({});
  const [permanentAddress, setPermanentAddress] = useState<any>({});

  const { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } = UseAppToast();

  // Reset marriage registration state when drawer closes or view changes
  useEffect(() => {
    if (!isOpen || view !== "MARRIAGE_REGISTRATION") {
      setMarriageStep(1);
      setRegisteringFor("SELF");
      setCandidateRelationship("");
      setCandidateName("");
      setHasPermission(false);
      setAcceptsTerms(false);
      setVerifyDob("");
      setVerifyEmailId("");
      setVerifyMobileNo("");
      setIsVerifyingProfile(false);
    }
  }, [isOpen, view]);

  // Prefill SELF verification fields from user profile
  useEffect(() => {
    if (!isOpen || view !== "MARRIAGE_REGISTRATION") return;

    // Prefill happens only after user proceeds (step 2) and only for SELF.
    if (registeringFor === "SELF" && marriageStep === 2) {
      const pd =
        (userData as any)?.personalDetails ||
        (userData as any)?.userDetails?.personalDetails ||
        (userData as any)?.profile?.personalDetails ||
        {};

      const cd =
        (userData as any)?.communicationDetails ||
        (userData as any)?.userDetails?.communicationDetails ||
        (userData as any)?.profile?.communicationDetails ||
        {};

      const rawName = [pd?.firstName, pd?.lastName].filter(Boolean).join(" ");

      // If the computed name is duplicated (e.g., "Aman Singh Aman Singh"), collapse to a single instance.
      const name = (() => {
        const n = String(rawName || "").trim().replace(/\s+/g, " ");
        if (!n) return "";
        const parts = n.split(" ");
        if (parts.length % 2 !== 0) return n;
        const half = parts.length / 2;
        const first = parts.slice(0, half).join(" ");
        const second = parts.slice(half).join(" ");
        return first.toLowerCase() === second.toLowerCase() ? first : n;
      })();

      // Fallbacks from localStorage accountInfo (commonly present)
      let accountEmail = "";
      let accountMobile = "";
      try {
        const raw = window.localStorage.getItem("accountInfo");
        const parsed = raw ? JSON.parse(raw) : null;
        accountEmail = String(parsed?.emailId || parsed?.email || "");
        accountMobile = String(parsed?.mobileNumber || parsed?.mobileNo || parsed?.mobile || "");
      } catch {
        // ignore
      }

      setCandidateName(name);
      setVerifyDob(pd?.dob || "");
      setVerifyEmailId(cd?.emailId || cd?.email || accountEmail || "");
      setVerifyMobileNo(cd?.mobileNumber || cd?.mobileNo || accountMobile || "");
    }

    // When FAMILY is selected, nothing should be auto-populated.
    if (registeringFor === "FAMILY" && marriageStep === 2) {
      setCandidateName("");
      setVerifyDob("");
      setVerifyEmailId("");
      setVerifyMobileNo("");
    }

    // If user goes back to step 1, don't keep stale values.
    if (marriageStep === 1) {
      setCandidateName("");
      setVerifyDob("");
      setVerifyEmailId("");
      setVerifyMobileNo("");
    }
  }, [isOpen, view, registeringFor, marriageStep, userData]);

  // Load data when userData changes
  useEffect(() => {
    if (userData) {
      setFormData(userData);
      const comm = (userData as any)?.communicationDetails || {};
      const curr = comm?.currentAddress || {};
      const perm = comm?.permanentAddress || {};
      setCurrentAddress({
        addressLine1: curr?.addressLine1 || "",
        addressLine2: curr?.addressLine2 || "",
        villageCity: curr?.villageCity || "",
        state: curr?.state || comm?.state || "",
        postOffice: curr?.postOffice || "",
        pincode: curr?.pincode || comm?.pinCode || "",
      });
      setPermanentAddress({
        addressLine1: perm?.addressLine1 || "",
        addressLine2: perm?.addressLine2 || "",
        villageCity: perm?.villageCity || "",
        state: perm?.state || comm?.state || "",
        postOffice: perm?.postOffice || "",
        pincode: perm?.pincode || comm?.pinCode || "",
      });
    }
  }, [userData]);

  // Prefill member fields when editing
  useEffect(() => {
    const isEditMember = view === "EDIT_MEMBER";
    if (isOpen && isEditMember && selectedMember) {
      setMemberName(selectedMember.memberName || "");
      setRelation(selectedMember.relation || "");
      setMemberEmail(selectedMember.email || "");
      setFamilyMobile(selectedMember.mobileNo || "");
    }
    if (!isOpen) {
      // Reset fields when drawer closes
      setMemberName("");
      setRelation("");
      setMemberEmail("");
      setFamilyMobile("");
    }
  }, [isOpen, view, selectedMember]);

  // Safely update nested values (kept as requested)
  const handleNestedChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    section?: string
  ) => {
    const { id, value } = e.target;
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...(prev as any)[section],
          [id]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...(prev as any), [id]: value }));
    }
  };

  // ---- Validation helpers ----
  const isValidMobile = (val: string | undefined | null): boolean => {
    if (!val || !val.trim()) return true; // empty is allowed (not-required fields)
    const digits = val.trim().replace(/\D/g, "");
    return digits.length === 10;
  };

  const isValidEmail = (val: string | undefined | null): boolean => {
    if (!val || !val.trim()) return true; // empty is allowed
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
  };

  const validateCommunicationDetails = (): string | null => {
    const comm = formData.communicationDetails || {};
    const mobile = comm.mobileNumber || "";
    const altMobile = (comm as any).altMobileNumber || "";
    const altEmail = (comm as any).altEmailId || "";

    if (mobile && !isValidMobile(mobile)) {
      return "Mobile number must be exactly 10 digits";
    }
    if (altMobile && !isValidMobile(altMobile)) {
      return "Alternate mobile number must be exactly 10 digits";
    }
    if (altEmail && !isValidEmail(altEmail)) {
      return "Please enter a valid alternate email address";
    }
    return null;
  };

  // Save handler
  const handleSave = async () => {
    try {
      const accountInfo = window.localStorage.getItem("accountInfo");
      const emailId = (accountInfo && JSON.parse(accountInfo || "{}")?.emailId) || "";
      if (!emailId) throw new Error("Missing emailId");

      // Validate communication fields before calling backend
      if (view === "COMMUNICATION_DETAILS") {
        const validationError = validateCommunicationDetails();
        if (validationError) {
          showWarningToast(validationError);
          return;
        }
      }

      const isFamilyView = view === "FAMILY" || view === "EDIT_MEMBER";

      if (isFamilyView) {
        if (!memberName || !relation) throw new Error("Member name and relation are required");

        // When editing, selectedMember will carry an id set in Family.tsx
        const existingId = (selectedMember as any)?.id as number | undefined;

        let resultMsg: string;
        if (existingId) {
          // Update existing family member
          resultMsg = await ProfileService.updateFamilyMember(existingId, {
            memberName,
            relation,
            email: memberEmail || null,
            mobileNo: familyMobile || null,
          });
        } else {
          // Add new family member
          resultMsg = await ProfileService.addFamilyMember(emailId, {
            memberName,
            relation,
            email: memberEmail || null || undefined,
            mobileNo: familyMobile || null || undefined,
          });
        }

        const refreshed = await ProfileService.getProfile(emailId);
        onSave?.(refreshed);
        showSuccessToast(resultMsg || "Saved successfully");
        onClose();
        return;
      }

      // If editing Address view, attach top-level currentAddress and permanentAddress per UpdateProfileDto
      if (view === "ADDRESS") {
        const updated: ProfileDto = {
          ...(formData as any),
          // Send addresses at top level to match backend ProfileUpdater expectations
          currentAddress: { ...currentAddress },
          permanentAddress: { ...permanentAddress },
        } as any;
        const updatedProfile = await ProfileService.updateProfile(emailId, updated);
        onSave?.(updatedProfile);
        showSuccessToast("Address updated");
        onClose();
        return;
      }

      const updatedProfile = await ProfileService.updateProfile(emailId, formData);
      onSave?.(updatedProfile);
      showSuccessToast("Profile updated successfully");
      onClose();
    } catch (err: any) {
      showErrorToast(err?.message || "Unable to save changes.");
    }
  };

  const handleMarriageProceed = async () => {
    const isFamily = registeringFor === "FAMILY";

    // Consultant form: no step logic — validate all 4 fields directly
    if (!isConsultant) {
      // Step 1 -> Step 2
      if (marriageStep === 1) {
        setMarriageStep(2);
        return;
      }
    }

    // --- Validation (step 2 for regular users, single-step for consultant) ---
    const fullName = (candidateName || "").trim();
    const dateOfBirth = (verifyDob || "").trim();
    const emailId = (verifyEmailId || "").trim();
    const mobileNo = (verifyMobileNo || "").trim();

    if (!fullName || !dateOfBirth) {
      showWarningToast("Candidate name and date of birth are required");
      return;
    }

    // Prevent future dates
    const dobDate = new Date(dateOfBirth);
    if (!Number.isNaN(dobDate.getTime()) && dobDate > new Date()) {
      showWarningToast("Date of birth cannot be a future date");
      return;
    }

    // Email format validation for consultant
    const isValidEmail = (val: string | undefined | null): boolean => {
      if (!val || !val.trim()) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
    };
    if (isConsultant && !isValidEmail(emailId)) {
      showWarningToast("Please enter a valid email address");
      return;
    }

    if (!emailId || !mobileNo) {
      showWarningToast("Email and mobile number are required");
      return;
    }

    if (!isConsultant && isFamily && !candidateRelationship) {
      showWarningToast("Relationship is required");
      return;
    }

    // Permission required for consultant (always) and non-consultant registering for a family member
    if ((isConsultant || isFamily) && !hasPermission) {
      showWarningToast("Permission is required to create this profile");
      return;
    }

    if (!acceptsTerms) {
      showWarningToast("Please accept terms and conditions");
      return;
    }

    const persistDraftAndProceed = () => {
      window.localStorage.setItem(
        "matrimonyProfileDraft",
        JSON.stringify({
          createdAt: Date.now(),
          registeringFor,
          candidateRelationship: isFamily ? candidateRelationship : "Self",
          candidateName: fullName,
          status: "DRAFT",
          verified: {
            candidateName: fullName,
            dateOfBirth,
            // IMPORTANT: backend expects `email` (not `emailId`)
            email: emailId,
            // IMPORTANT: backend + registration prefill expect `mobileNo`
            mobileNo: mobileNo,
          },
        })
      );

      onClose();
      navigate(AppRoutes.MATRIMONY_FORM);
    };

    try {
      setIsVerifyingProfile(true);

      // Backend contract:
      // - 200 => returns existing Marriage Profile object (profile exists)
      // - 404 => { message: "Marriage Profile not exist" } (profile does not exist)
      const existingProfile = await MarriageProfileService.verifyExists({
        fullName,
        dateOfBirth,
        // IMPORTANT: verify API expects `email`
        email: emailId,
        // IMPORTANT: verify API expects `mobileNo`
        mobileNo: mobileNo,
      });

      // If we got here, request succeeded => profile exists => DO NOT proceed.
      showInfoToast("Marriage profile already exists");

      // Optional: keep a reference for future improvements (e.g., deep-link to details)
      // window.localStorage.setItem("existingMarriageProfile", JSON.stringify(existingProfile));
      void existingProfile;
      return;
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = String(err?.response?.data?.message || err?.message || "");
      const normalized = msg.toLowerCase();

      // Proceed ONLY when backend says "not found".
      if (
        status === 404 ||
        normalized.includes("not exist") ||
        normalized.includes("does not exist") ||
        normalized.includes("profile not exist") ||
        normalized.includes("no marriage profile") ||
        normalized.includes("not found")
      ) {
        persistDraftAndProceed();
        return;
      }

      showErrorToast(msg || "Unable to verify marriage profile.");
    } finally {
      setIsVerifyingProfile(false);
    }
  };

  // Add Family Members form
  const addFamilyMembers = (): React.ReactNode => {
    return (
      <Stack spacing={4}>
        <FormControl id="memberName" isRequired>
          <FormLabel>Member Name</FormLabel>
          <Input type="text" placeholder="Enter member name" value={memberName} onChange={(e)=>setMemberName(e.target.value)} />
        </FormControl>
        <FormControl id="relation" isRequired>
          <FormLabel>Relation</FormLabel>
          <Input type="text" placeholder="Enter relation" value={relation} onChange={(e)=>setRelation(e.target.value)} />
        </FormControl>
        <FormControl id="email">
          <FormLabel>Email</FormLabel>
          <Input type="email" placeholder="Enter email" value={memberEmail} onChange={(e)=>setMemberEmail(e.target.value)} />
        </FormControl>
        <FormControl id="mobileNo">
          <FormLabel>Mobile No</FormLabel>
          <MobileNumberInput mobileNumber={familyMobile} setMobileNumber={setFamilyMobile} />
        </FormControl>
      </Stack>
    );
  };

  // Communication Details form
  const editCommunicationDetails = (): React.ReactNode => {
    const comm = formData.communicationDetails || {};
    const mobileVal = comm.mobileNumber || "";
    const altMobileVal = (comm as any).altMobileNumber || "";
    const altEmailVal = (comm as any).altEmailId || "";

    const mobileErr = mobileVal && !isValidMobile(mobileVal) ? "Must be exactly 10 digits" : "";
    const altMobileErr = altMobileVal && !isValidMobile(altMobileVal) ? "Must be exactly 10 digits" : "";
    const altEmailErr = altEmailVal && !isValidEmail(altEmailVal) ? "Please enter a valid email address" : "";

    return (
      <Stack spacing={4}>
        <FormControl id="mobileNumber" isRequired isInvalid={!!mobileErr}>
          <FormLabel>Contact No</FormLabel>
          <Input
            type="tel"
            placeholder="Enter contact number"
            maxLength={10}
            value={mobileVal}
            onChange={(e) => handleNestedChange(e, "communicationDetails")}
          />
          {mobileErr && <FormErrorMessage>{mobileErr}</FormErrorMessage>}
        </FormControl>
        <FormControl id="altMobileNumber" isInvalid={!!altMobileErr}>
          <FormLabel>Alternative Contact No</FormLabel>
          <Input
            type="tel"
            placeholder="Enter alternative contact number"
            maxLength={10}
            value={altMobileVal}
            onChange={(e) => handleNestedChange(e, "communicationDetails")}
          />
          {altMobileErr && <FormErrorMessage>{altMobileErr}</FormErrorMessage>}
        </FormControl>
        {/* Email ID field commented out per request:
        <FormControl id="email" isRequired>
          <FormLabel>Email ID</FormLabel>
          <Input
            type="email"
            placeholder="Enter email ID"
            value={formData.communicationDetails?.email || ""}
            onChange={(e) => handleNestedChange(e, "communicationDetails")}
          />
        </FormControl>
        */}
        <FormControl id="altEmailId" isInvalid={!!altEmailErr}>
          <FormLabel>Alternative Email ID</FormLabel>
          <Input
            type="email"
            placeholder="Enter alternative email ID"
            value={altEmailVal}
            onChange={(e) => handleNestedChange(e, "communicationDetails")}
          />
          {altEmailErr && <FormErrorMessage>{altEmailErr}</FormErrorMessage>}
        </FormControl>
      </Stack>
    );
  };

  // About form
  const editAbout = (): React.ReactNode => {
    return (
      <Stack spacing={4}>
        <FormControl id="bio">
          <FormLabel>Bio</FormLabel>
          <Textarea
            placeholder="Write a brief bio"
            value={(formData as any).bio || ""}
            onChange={(e) => handleNestedChange(e)}
          />
        </FormControl>
        <FormControl id="maritalStatus" isRequired>
          <FormLabel>Marital Status</FormLabel>
          <Select
            placeholder="Select marital status"
            value={formData.personalDetails?.maritalStatus || ""}
            onChange={(e) => handleNestedChange(e, "personalDetails")}
          >
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Divorced">Divorced</option>
          </Select>
        </FormControl>
      </Stack>
    );
  };

  // Profession form
  const editProfession = (): React.ReactNode => {
    return (
      <Stack spacing={4}>
        <FormControl id="occupation" isRequired>
          <FormLabel>Occupation</FormLabel>
          <Input
            type="text"
            placeholder="Enter occupation"
            value={formData.businessDetails?.occupation || ""}
            onChange={(e) => handleNestedChange(e, "businessDetails")}
          />
        </FormControl>
        <FormControl id="organization" isRequired>
          <FormLabel>Organization</FormLabel>
          <Input type="text" placeholder="Enter organization name" />
        </FormControl>
        <FormControl id="educationalQualification" isRequired>
          <FormLabel>Educational Qualification</FormLabel>
          <Input
            type="text"
            placeholder="Enter your highest qualification"
            value={formData.businessDetails?.educationalQualification || ""}
            onChange={(e) => handleNestedChange(e, "businessDetails")}
          />
        </FormControl>
        <FormControl id="university" isRequired>
          <FormLabel>University</FormLabel>
          <Input type="text" placeholder="Enter university name" />
        </FormControl>
        <FormControl id="year-of-graduation" isRequired>
          <FormLabel>Year of Graduation</FormLabel>
          <Input type="number" placeholder="Enter year of graduation" />
        </FormControl>
        <FormControl id="business">
          <FormLabel>Business Details</FormLabel>
          <Textarea
            placeholder="Provide details about your business (if applicable)"
            value={formData.businessDetails?.business || ""}
            onChange={(e) => handleNestedChange(e, "businessDetails")}
          />
        </FormControl>
      </Stack>
    );
  };

  // Address form
  const editAddress = (): React.ReactNode => {
    return (
      <Stack spacing={4}>
        <Text fontSize="18px" fontWeight="700">Current Address</Text>
        <FormControl id="addressLine1" isRequired>
          <FormLabel>Street Address Line 1</FormLabel>
          <Input
            type="text"
            placeholder="Enter street address line 1"
            value={currentAddress.addressLine1 || ""}
            onChange={(e) => setCurrentAddress((p: any) => ({ ...p, addressLine1: e.target.value }))}
          />
        </FormControl>
        <FormControl id="addressLine2">
          <FormLabel>Street Address Line 2</FormLabel>
          <Input
            type="text"
            placeholder="Enter street address line 2"
            value={currentAddress.addressLine2 || ""}
            onChange={(e) => setCurrentAddress((p: any) => ({ ...p, addressLine2: e.target.value }))}
          />
        </FormControl>
        <HStack spacing={4}>
          <FormControl id="villageCity" isRequired>
            <FormLabel>Village/City</FormLabel>
            <Input
              type="text"
              placeholder="Enter village/city"
              value={currentAddress.villageCity || ""}
              onChange={(e) => setCurrentAddress((p: any) => ({ ...p, villageCity: e.target.value }))}
            />
          </FormControl>
          <FormControl id="state" isRequired>
            <FormLabel>State & District</FormLabel>
            <Input
              type="text"
              placeholder="Enter state & district"
              value={currentAddress.state || ""}
              onChange={(e) => setCurrentAddress((p: any) => ({ ...p, state: e.target.value }))}
            />
          </FormControl>
        </HStack>
        <HStack spacing={4}>
          <FormControl id="postOffice">
            <FormLabel>Post Office</FormLabel>
            <Input
              type="text"
              placeholder="Enter post office"
              value={currentAddress.postOffice || ""}
              onChange={(e) => setCurrentAddress((p: any) => ({ ...p, postOffice: e.target.value }))}
            />
          </FormControl>
          <FormControl id="pincode" isRequired>
            <FormLabel>Pin Code</FormLabel>
            <Input
              type="text"
              placeholder="Enter pin code"
              value={currentAddress.pincode || ""}
              onChange={(e) => setCurrentAddress((p: any) => ({ ...p, pincode: e.target.value }))}
            />
          </FormControl>
        </HStack>
        <Text fontSize="18px" fontWeight="700">Permanent Address</Text>
        <FormControl id="perm-addressLine1">
          <FormLabel>Street Address Line 1</FormLabel>
          <Input
            type="text"
            placeholder="Enter street address line 1"
            value={permanentAddress.addressLine1 || ""}
            onChange={(e) => setPermanentAddress((p: any) => ({ ...p, addressLine1: e.target.value }))}
          />
        </FormControl>
        <FormControl id="perm-addressLine2">
          <FormLabel>Street Address Line 2</FormLabel>
          <Input
            type="text"
            placeholder="Enter street address line 2"
            value={permanentAddress.addressLine2 || ""}
            onChange={(e) => setPermanentAddress((p: any) => ({ ...p, addressLine2: e.target.value }))}
          />
        </FormControl>
        <HStack spacing={4}>
          <FormControl id="perm-villageCity" isRequired>
            <FormLabel>Village/City</FormLabel>
            <Input
              type="text"
              placeholder="Enter village/city"
              value={permanentAddress.villageCity || ""}
              onChange={(e) => setPermanentAddress((p: any) => ({ ...p, villageCity: e.target.value }))}
            />
          </FormControl>
          <FormControl id="perm-state" isRequired>
            <FormLabel>State & District</FormLabel>
            <Input
              type="text"
              placeholder="Enter state & district"
              value={permanentAddress.state || ""}
              onChange={(e) => setPermanentAddress((p: any) => ({ ...p, state: e.target.value }))}
            />
          </FormControl>
        </HStack>
        <HStack spacing={4}>
          <FormControl id="perm-postOffice">
            <FormLabel>Post Office</FormLabel>
            <Input
              type="text"
              placeholder="Enter post office"
              value={permanentAddress.postOffice || ""}
              onChange={(e) => setPermanentAddress((p: any) => ({ ...p, postOffice: e.target.value }))}
            />
          </FormControl>
          <FormControl id="perm-pincode" isRequired>
            <FormLabel>Pin Code</FormLabel>
            <Input
              type="text"
              placeholder="Enter pin code"
              value={permanentAddress.pincode || ""}
              onChange={(e) => setPermanentAddress((p: any) => ({ ...p, pincode: e.target.value }))}
            />
          </FormControl>
        </HStack>
      </Stack>
    );
  };

  // Marriage Registration 2-step form
  const renderMarriageRegistration = (): React.ReactNode => {
    const isFamily = registeringFor === "FAMILY";

    // ── Consultant simplified form ──────────────────────────────────
    if (isConsultant) {
      return (
        <Stack spacing={6}>
          <FormControl isRequired>
            <FormLabel>Candidate Name</FormLabel>
            <Input
              placeholder="Enter candidate name"
              value={candidateName}
              onChange={(e) => setCandidateName(e.target.value)}
              bg="#F8F9FA"
              borderRadius="12px"
              borderColor="rgba(108, 117, 125, 0.3)"
              height="48px"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Date of Birth</FormLabel>
            <Input
              type="date"
              max={new Date().toISOString().split("T")[0]}
              value={verifyDob}
              onChange={(e) => setVerifyDob(e.target.value)}
              bg="#F8F9FA"
              borderRadius="12px"
              borderColor="rgba(108, 117, 125, 0.3)"
              height="48px"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Email ID</FormLabel>
            <Input
              type="email"
              placeholder="Enter email ID"
              value={verifyEmailId}
              onChange={(e) => setVerifyEmailId(e.target.value)}
              bg="#F8F9FA"
              borderRadius="12px"
              borderColor="rgba(108, 117, 125, 0.3)"
              height="48px"
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Mobile Number</FormLabel>
            <MobileNumberInput mobileNumber={verifyMobileNo} setMobileNumber={setVerifyMobileNo} />
          </FormControl>

          <Stack spacing={3} mt={2}>
            <Checkbox
              sx={{
                "& .chakra-checkbox__control": { borderRadius: "0", borderColor: "#FF7E6B" },
                "& .chakra-checkbox__control[data-checked]": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                "& .chakra-checkbox__control[data-checked]:hover": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                "& .chakra-checkbox__control:hover": { bg: "#FF7E6B" },
              }}
              isChecked={hasPermission}
              onChange={(e) => setHasPermission(e.target.checked)}
            >
              <Text as="span" color="#6C757D">
                I have permission to create this profile
              </Text>
            </Checkbox>

            <Checkbox
              sx={{
                "& .chakra-checkbox__control": { borderRadius: "0", borderColor: "#FF7E6B" },
                "& .chakra-checkbox__control[data-checked]": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                "& .chakra-checkbox__control[data-checked]:hover": { bg: "#FF7E6B", borderColor: "#FF7E6B" },
                "& .chakra-checkbox__control:hover": { bg: "#FF7E6B" },
              }}
              isChecked={acceptsTerms}
              onChange={(e) => setAcceptsTerms(e.target.checked)}
            >
              <Text as="span" color="#6C757D">
                I understand{" "}
              </Text>
              <Text as="span" fontWeight="500" color="#1F0812">
                terms and conditions
              </Text>
            </Checkbox>
          </Stack>
        </Stack>
      );
    }

    // ── Regular user 2-step form ────────────────────────────────────
    return (
      <Stack spacing={6}>
        <FormControl>
          <FormLabel>
            I am registering for
          </FormLabel>
          <Select
            value={registeringFor}
            onChange={(e) =>
              setRegisteringFor(e.target.value === "FAMILY" ? "FAMILY" : "SELF")
            }
            bg="#F8F9FA"
            borderRadius="12px"
            borderColor="rgba(108, 117, 125, 0.3)"
            height="48px"
          >
            <option value="SELF">For Myself</option>
            <option value="FAMILY">For a Family Member</option>
          </Select>
        </FormControl>

        {marriageStep === 2 && isFamily && (
          <>
            <FormControl>
              <FormLabel>
                Relationship
              </FormLabel>
              <Select
                placeholder="Select relationship"
                value={candidateRelationship}
                onChange={(e) => setCandidateRelationship(e.target.value)}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
              >
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {marriageStep === 2 && (
          <>
            <FormControl>
              <FormLabel>Candidate Name</FormLabel>
              <Input
                placeholder="Enter candidate name"
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Date of Birth</FormLabel>
              <Input
                type="date"
                placeholder="Enter date of birth"
                max={new Date().toISOString().split("T")[0]}
                value={verifyDob}
                onChange={(e) => setVerifyDob(e.target.value)}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email ID</FormLabel>
              <Input
                type="email"
                placeholder="Enter email ID"
                value={verifyEmailId}
                onChange={(e) => setVerifyEmailId(e.target.value)}
                bg="#F8F9FA"
                borderRadius="12px"
                borderColor="rgba(108, 117, 125, 0.3)"
                height="48px"
                isDisabled={registeringFor === "SELF"}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Mobile Number</FormLabel>
              {registeringFor === "SELF" ? (
                <Input
                  type="tel"
                  placeholder="Enter mobile number"
                  value={verifyMobileNo}
                  onChange={(e) => setVerifyMobileNo(e.target.value)}
                  bg="#F8F9FA"
                  borderRadius="12px"
                  borderColor="rgba(108, 117, 125, 0.3)"
                  height="48px"
                  isDisabled
                />
              ) : (
                <MobileNumberInput mobileNumber={verifyMobileNo} setMobileNumber={setVerifyMobileNo} />
              )}
            </FormControl>
          </>
        )}

        {marriageStep === 2 && <Stack spacing={3} mt={2}>
          {isFamily && (
            <Checkbox
              sx={{
                "& .chakra-checkbox__control": {
                  borderRadius: "0",
                  borderColor: "#FF7E6B"
                },
                "& .chakra-checkbox__control[data-checked]": {
                  bg: "#FF7E6B",
                  borderColor: "#FF7E6B"
                },
                "& .chakra-checkbox__control[data-checked]:hover": {
                  bg: "#FF7E6B",
                  borderColor: "#FF7E6B"
                },
                "& .chakra-checkbox__control:hover": {
                  bg: "#FF7E6B",
                },
              }}
              isChecked={hasPermission}
              onChange={(e) => setHasPermission(e.target.checked)}
            >
              <Text as="span" color="#6C757D">
                I have permission to create this profile
              </Text>
            </Checkbox>
          )}

          <Checkbox
            sx={{
                "& .chakra-checkbox__control": {
                  borderRadius: "0",
                  borderColor: "#FF7E6B"
                },
                "& .chakra-checkbox__control[data-checked]": {
                  bg: "#FF7E6B",
                  borderColor: "#FF7E6B"
                },
                "& .chakra-checkbox__control[data-checked]:hover": {
                  bg: "#FF7E6B",
                  borderColor: "#FF7E6B"
                },
                "& .chakra-checkbox__control:hover": {
                  bg: "#FF7E6B",
                },
              }}
            isChecked={acceptsTerms}
            onChange={(e) => setAcceptsTerms(e.target.checked)}
          >
            <Text as="span" color="#6C757D">
              I understand{" "}
            </Text>
            <Text as="span" fontWeight="500" color="#1F0812">
              terms and conditions
            </Text>
          </Checkbox>
        </Stack>}
      </Stack>
    );
  };

  // Switch body selector
const getBody = () => {
  switch (view) {
    case "ABOUT":
      return editAbout();

    case "ADDRESS":
      return editAddress();

    case "COMMUNICATION_DETAILS":
      return editCommunicationDetails();

    case "PROFESSIONAL_BACKGROUND":
      return editProfession();

    case "MARRIAGE_REGISTRATION":
      return renderMarriageRegistration();

    case "FAMILY":
    case "EDIT_MEMBER":
      return addFamilyMembers();

    default:
      return null;
  }
};

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={true} closeOnEsc={true}>
      <DrawerOverlay />
      <DrawerContent maxW={{ base: "100%", md: "50%", lg: "30%" }}>
        <DrawerCloseButton onClick={onClose} />
        <DrawerHeader> {isHindiLabels
          ? Profile_Tab_Hin[view as keyof typeof Profile_Tab_Hin]
          : Profile_Tab_English[view as keyof typeof Profile_Tab_English]}</DrawerHeader>

        <DrawerBody>{getBody()}</DrawerBody>

        {view === "MARRIAGE_REGISTRATION" ? (
          <Flex width="100%" justifyContent="flex-end" gap={4} p={3}>
            <Button
              variant="outline"
              padding="16px 28px"
              borderColor="#4A2C2A"
              color="#4A2C2A"
              _hover={{ backgroundColor: "rgba(74, 44, 42, 0.06)" }}
              onClick={onClose}
            >
              Never Mind
            </Button>
            <Button
              padding="16px 28px"
              color="white"
              backgroundColor="#4A2C2A"
              _hover={{ backgroundColor: "gray.600" }}
              rightIcon={<ArrowRightIcon />}
              onClick={handleMarriageProceed}
              isLoading={isVerifyingProfile}
            >
              {EnglishLabels.Proceed}
            </Button>
          </Flex>
        ) : (
          <Flex width="100%" justifyContent="space-between" p={3}>
            <Button
              padding="16px 28px"
              color="white"
              backgroundColor="#4A2C2A"
              _hover={{ backgroundColor: "gray.600" }}
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              padding="16px 28px"
              color="white"
              backgroundColor="#4A2C2A"
              _hover={{ backgroundColor: "gray.600" }}
              onClick={onClose}
            >
              Close
            </Button>
          </Flex>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default RightAlignedDrawer;
